import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { Integration } from '@gitbook/api-client';
import { Alert } from '@gitbook/spine-react';

import { useNavigation } from '../../../navigation';
import { apiQuery } from '../../../state';
import { IntegrationIcon } from '../..';
import { ContentEditorContext } from '../contexts';

/**
 * Alert to display when a block for an integration has been created but the integration is not configured.
 */
export function MisconfiguredIntegrationAlert(props: {
    integrationName: string;
}): React.ReactElement {
    const { integrationName } = props;

    const { data: integration } = useSuspenseQuery(
        apiQuery('getIntegrationByName', { integrationName }, { nullOnError: true })
    );
    if (integration) {
        return <MisconfiguredIntegrationAlertWithData integration={integration} />;
    }

    return (
        <Alert
            kind="default"
            title={`Integration ${integrationName} no longer exist or has been unpublished.`}
        />
    );
}

function MisconfiguredIntegrationAlertWithData(props: {
    integration: Integration;
}): React.ReactElement {
    const { integration } = props;
    const { navigate } = useNavigation();
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    return (
        <Alert
            kind="default"
            icon={<IntegrationIcon integration={integration} />}
            title={`Integration ${integration.title} is not configured. Install it to get started.`}
            action={{
                label: 'Install',
                onPress: () => {
                    logAnalyticsEvent({
                        type: 'edit_open_integration',
                        integration: integration.name,
                        reason: 'empty',
                    });

                    navigate({
                        current: {
                            sideSheet: {
                                type: 'integrations',
                                view: { type: 'integration', integration: integration.name },
                            },
                        },
                    });
                },
            }}
        />
    );
}
